import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Users,
  Search,
  Filter,
  Eye,
  Ban,
  CheckCircle,
  Heart,
  Calendar,
  Phone,
  Shield,
  X,
  FileText,
  Download,
  Smartphone
} from 'lucide-react';
import { adminUsersApi } from '../utils/apiService';
import { toast } from 'sonner';

interface User {
  id: string;
  name: string;
  profilePicture: string;
  phone: string;
  joinedDate: string;
  status: 'active' | 'suspended' | 'banned';
  stats: {
    thoughtsRead: number;
    currentStreak: number;
    longestStreak: number;
  };
  lastActive: string;
  isAdmin: boolean;
  deviceInfo?: {
    platform?: string;
    model?: string;
    osVersion?: string;
    appVersion?: string;
    fcmToken?: string;
  };
}

export default function UserManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'suspended' | 'banned'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [_isLoading, setIsLoading] = useState(false);
  const [currentPage] = useState(1);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const params: any = {
          page: currentPage,
          limit: 50,
          isAdmin: false
        };
        
        if (filterStatus !== 'all') {
          params.status = filterStatus;
        }
        
        if (searchQuery.trim()) {
          params.search = searchQuery;
        }

        const result = await adminUsersApi.getAll(params);
        console.log('Users API response:', result);

        // Map backend data to frontend format
        const usersArray = result.users || result.data || [];
        const mappedUsers = usersArray.map((user: any) => {
          const createdAt = user.createdAt ? new Date(user.createdAt) : null;
          const joinedDate = createdAt && !isNaN(createdAt.getTime())
            ? createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
            : 'Unknown';

          let status: 'active' | 'suspended' | 'banned' = 'active';
          if (user.isBanned) status = 'banned';
          else if (user.isSuspended) status = 'suspended';
          else if (user.status === 'banned') status = 'banned';
          else if (user.status === 'suspended') status = 'suspended';

          return {
            id: user._id,
            name: user.name || 'Anonymous User',
            profilePicture: user.profilePicture || '',
            phone: user.phoneNumber || 'N/A',
            joinedDate,
            status,
            stats: {
              thoughtsRead: user.stats?.thoughtsRead || 0,
              currentStreak: user.stats?.currentStreak || 0,
              longestStreak: user.stats?.longestStreak || 0,
            },
            lastActive: formatLastActive(user.lastActive || user.updatedAt),
            isAdmin: user.isAdmin || user.role === 'admin',
            deviceInfo: {
              platform: user.deviceInfo?.platform || user.platform || '',
              model: user.deviceInfo?.model || user.deviceModel || '',
              osVersion: user.deviceInfo?.osVersion || user.osVersion || '',
              appVersion: user.deviceInfo?.appVersion || user.appVersion || '',
              fcmToken: user.deviceInfo?.fcmToken || user.fcmToken || '',
            },
          };
        });

        setUsers(mappedUsers);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to load users. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [filterStatus, searchQuery, currentPage]);

  // Helper function to format last active time
  const formatLastActive = (lastActive: string) => {
    if (!lastActive) return 'Never';
    
    const now = new Date();
    const activeDate = new Date(lastActive);
    const diffMs = now.getTime() - activeDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return activeDate.toLocaleDateString();
  };

  const handleSuspend = async (userId: string) => {
    if (confirm('Are you sure you want to suspend this user?')) {
      try {
        await adminUsersApi.suspend(userId, 'Suspended by admin');
        setUsers(prev => prev.map(user =>
          user.id === userId ? { ...user, status: 'suspended' as const } : user
        ));
        toast.success('User suspended successfully');
      } catch (error) {
        console.error('Failed to suspend user:', error);
        toast.error('Failed to suspend user. Please try again.');
      }
    }
  };

  const handleBan = async (userId: string) => {
    if (confirm('Are you sure you want to permanently ban this user?')) {
      try {
        await adminUsersApi.ban(userId, 'Banned by admin');
        setUsers(prev => prev.map(user =>
          user.id === userId ? { ...user, status: 'banned' as const } : user
        ));
        toast.success('User banned successfully');
      } catch (error) {
        console.error('Failed to ban user:', error);
        toast.error('Failed to ban user. Please try again.');
      }
    }
  };

  const handleActivate = async (userId: string) => {
    try {
      await adminUsersApi.activate(userId);
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, status: 'active' as const } : user
      ));
      toast.success('User activated successfully');
    } catch (error) {
      console.error('Failed to activate user:', error);
      toast.error('Failed to activate user. Please try again.');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.phone.includes(searchQuery);
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    suspended: users.filter(u => u.status === 'suspended').length,
    banned: users.filter(u => u.status === 'banned').length
  };

  const downloadExcel = () => {
    const data = filteredUsers.map(user => ({
      Name: user.name,
      Phone: user.phone,
      Status: user.status.charAt(0).toUpperCase() + user.status.slice(1),
      'Last Active': user.lastActive,
      'Joined Date': user.joinedDate,
      'Thoughts Read': user.stats.thoughtsRead,
      'Current Streak': user.stats.currentStreak,
      'Longest Streak': user.stats.longestStreak,
      'Is Admin': user.isAdmin ? 'Yes' : 'No',
      Platform: user.deviceInfo?.platform || 'N/A',
      'Device Model': user.deviceInfo?.model || 'N/A',
      'OS Version': user.deviceInfo?.osVersion || 'N/A',
      'App Version': user.deviceInfo?.appVersion || 'N/A',
      'FCM Token': user.deviceInfo?.fcmToken || 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    XLSX.writeFile(workbook, `users_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">User Management</h2>
          <p className="text-slate-600">Manage and moderate app users</p>
        </div>
        <button
          onClick={downloadExcel}
          disabled={filteredUsers.length === 0}
          className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#bf9a2d] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          Download Excel
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Users</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <Users className="w-10 h-10 text-slate-400" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-green-200 bg-green-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700">Active Users</p>
              <p className="text-2xl font-bold text-green-800">{stats.active}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-700">Suspended</p>
              <p className="text-2xl font-bold text-amber-800">{stats.suspended}</p>
            </div>
            <Shield className="w-10 h-10 text-amber-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700">Banned</p>
              <p className="text-2xl font-bold text-red-800">{stats.banned}</p>
            </div>
            <Ban className="w-10 h-10 text-red-500" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-600" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-4 py-2.5 border-2 border-slate-200 rounded-xl bg-white focus:outline-none focus:border-[#2B7A7E] cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="banned">Banned</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">User</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Contact</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Device</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Last Active</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Activity</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    {users.length === 0 ? 'No users found. Check browser console for API response details.' : 'No users match your search/filter.'}
                  </td>
                </tr>
              )}
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-10 h-10 rounded-full bg-[#2B7A7E] flex items-center justify-center text-white font-semibold text-sm ${user.profilePicture ? 'hidden' : ''}`}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{user.name}</p>
                        {user.isAdmin && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            <Shield className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Phone className="w-4 h-4" />
                      {user.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.deviceInfo?.platform || user.deviceInfo?.model ? (
                      <div className="flex items-start gap-1.5 text-sm text-slate-600">
                        <Smartphone className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                          {user.deviceInfo.platform && (
                            <p className="font-medium capitalize">{user.deviceInfo.platform}</p>
                          )}
                          {user.deviceInfo.model && (
                            <p className="text-xs text-slate-400">{user.deviceInfo.model}</p>
                          )}
                          {user.deviceInfo.appVersion && (
                            <p className="text-xs text-slate-400">v{user.deviceInfo.appVersion}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Calendar className="w-4 h-4" />
                      {user.lastActive}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-slate-600" title="Thoughts Read">
                        <FileText className="w-4 h-4" />
                        {user.stats.thoughtsRead}
                      </div>
                      <div className="flex items-center gap-1 text-slate-600" title="Current Streak">
                        🔥 {user.stats.currentStreak}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.status === 'active' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
                        <CheckCircle className="w-4 h-4" />
                        Active
                      </span>
                    )}
                    {user.status === 'suspended' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold">
                        <Shield className="w-4 h-4" />
                        Suspended
                      </span>
                    )}
                    {user.status === 'banned' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-semibold">
                        <Ban className="w-4 h-4" />
                        Banned
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-5 h-5 text-slate-600" />
                      </button>
                      {!user.isAdmin && (
                        <>
                          {user.status === 'active' && (
                            <>
                              <button
                                onClick={() => handleSuspend(user.id)}
                                className="p-2 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Suspend User"
                              >
                                <Shield className="w-5 h-5 text-amber-600" />
                              </button>
                              <button
                                onClick={() => handleBan(user.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="Ban User"
                              >
                                <Ban className="w-5 h-5 text-red-600" />
                              </button>
                            </>
                          )}
                          {(user.status === 'suspended' || user.status === 'banned') && (
                            <button
                              onClick={() => handleActivate(user.id)}
                              className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                              title="Activate User"
                            >
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-slate-800">User Details</h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-slate-600" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                {selectedUser.profilePicture ? (
                  <img
                    src={selectedUser.profilePicture}
                    alt={selectedUser.name}
                    className="w-20 h-20 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`w-20 h-20 rounded-full bg-[#2B7A7E] flex items-center justify-center text-white font-bold text-2xl ${selectedUser.profilePicture ? 'hidden' : ''}`}>
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-slate-800">{selectedUser.name}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-600">{selectedUser.phone}</span>
                  </div>
                  {selectedUser.isAdmin && (
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-semibold">
                        <Shield className="w-4 h-4" />
                        Administrator
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <FileText className="w-8 h-8 text-[#2B7A7E] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">{selectedUser.stats.thoughtsRead}</p>
                  <p className="text-sm text-slate-600">Thoughts Read</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <span className="text-3xl block mb-2">🔥</span>
                  <p className="text-2xl font-bold text-slate-800">{selectedUser.stats.currentStreak}</p>
                  <p className="text-sm text-slate-600">Current Streak</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">{selectedUser.stats.longestStreak}</p>
                  <p className="text-sm text-slate-600">Longest Streak</p>
                </div>
              </div>

              {/* Account Info */}
              <div className="space-y-3">
                <h5 className="font-semibold text-slate-800">Account Information</h5>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Last Active:</span>
                    <span className="font-medium text-slate-800">{selectedUser.lastActive}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Joined:</span>
                    <span className="font-medium text-slate-800">{selectedUser.joinedDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Status:</span>
                    <span className={`font-medium ${
                      selectedUser.status === 'active' ? 'text-green-600' :
                      selectedUser.status === 'suspended' ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Device Details */}
              <div className="space-y-3">
                <h5 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Device Details
                </h5>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  {selectedUser.deviceInfo?.platform || selectedUser.deviceInfo?.model ? (
                    <>
                      {selectedUser.deviceInfo.platform && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Platform:</span>
                          <span className="font-medium text-slate-800 capitalize">{selectedUser.deviceInfo.platform}</span>
                        </div>
                      )}
                      {selectedUser.deviceInfo.model && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Device Model:</span>
                          <span className="font-medium text-slate-800">{selectedUser.deviceInfo.model}</span>
                        </div>
                      )}
                      {selectedUser.deviceInfo.osVersion && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">OS Version:</span>
                          <span className="font-medium text-slate-800">{selectedUser.deviceInfo.osVersion}</span>
                        </div>
                      )}
                      {selectedUser.deviceInfo.appVersion && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">App Version:</span>
                          <span className="font-medium text-slate-800">v{selectedUser.deviceInfo.appVersion}</span>
                        </div>
                      )}
                      {selectedUser.deviceInfo.fcmToken && (
                        <div className="flex justify-between gap-4">
                          <span className="text-slate-600 shrink-0">FCM Token:</span>
                          <span className="font-mono text-xs text-slate-700 break-all text-right">{selectedUser.deviceInfo.fcmToken}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-400 text-sm">No device information available</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              {!selectedUser.isAdmin && (
                <div className="flex gap-3">
                  {selectedUser.status === 'active' && (
                    <>
                      <button
                        onClick={() => {
                          handleSuspend(selectedUser.id);
                          setSelectedUser(null);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
                      >
                        <Shield className="w-5 h-5" />
                        Suspend User
                      </button>
                      <button
                        onClick={() => {
                          handleBan(selectedUser.id);
                          setSelectedUser(null);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                      >
                        <Ban className="w-5 h-5" />
                        Ban User
                      </button>
                    </>
                  )}
                  {(selectedUser.status === 'suspended' || selectedUser.status === 'banned') && (
                    <button
                      onClick={() => {
                        handleActivate(selectedUser.id);
                        setSelectedUser(null);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Activate User
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Results Summary */}
      <div className="text-center text-sm text-slate-600">
        Showing {filteredUsers.length} of {users.length} total users
      </div>
    </div>
  );
}
